import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ParticipationCampaign } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, Spinner, StatusBadge } from '../../ui/components';

/**
 * DRAFT -> OPEN -> CLOSED -> FINALIZED lifecycle (CANCELLED reachable from
 * DRAFT/OPEN) — see m41-local-government/src/routes/campaigns.ts. Finalize
 * ranks SHORTLISTED proposals by vote count and greedily funds them within
 * the budget envelope (service/campaignFinalize.ts#selectProjectsByVote),
 * publishes an OpenGov gazette notice, and notifies every proposer — a real
 * side-effecting call, not a client-side simulation, so it needs C11 OpenGov
 * and C4 SeyNotify reachable (OPENGOV_URL/NOTIFY_URL on the m41 process) or
 * it will surface a real 500 here rather than pretend to succeed.
 */
export function CampaignDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { persona } = useAuth();
  const isAdmin = persona?.roles.includes('DISTRICT_ADMINISTRATOR') ?? false;

  const campaignQ = useApiQuery<ParticipationCampaign>(`/api/v1/m41/campaigns/${id}`);
  const campaign = campaignQ.data;

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [finalizeResult, setFinalizeResult] = useState<{ selectedIds: string[]; notSelectedIds: string[] } | null>(null);

  const refresh = () => {
    campaignQ.reload();
    onChanged();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const open = () => run(() => api.post(`/api/v1/m41/campaigns/${id}/open`));
  const close = () => run(() => api.post(`/api/v1/m41/campaigns/${id}/close`));
  const cancel = () =>
    run(async () => {
      await api.post(`/api/v1/m41/campaigns/${id}/cancel`, { reason: cancelReason.trim() });
      setShowCancel(false);
      setCancelReason('');
    });
  const finalize = () =>
    run(async () => {
      const r = await api.post<ParticipationCampaign & { selectedIds: string[]; notSelectedIds: string[] }>(`/api/v1/m41/campaigns/${id}/finalize`);
      setFinalizeResult({ selectedIds: r.selectedIds, notSelectedIds: r.notSelectedIds });
    });

  const projects = campaign?.projects ?? [];
  const maxVotes = Math.max(1, ...projects.map((p) => p.voteCount));

  return (
    <Modal title={campaign ? campaign.title : 'Campaign'} onClose={onClose} wide>
      {campaignQ.loading ? <Spinner /> : null}
      {campaignQ.error ? <ErrorNote message={campaignQ.error} /> : null}
      {actionError ? (
        <div className="mb-3">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {campaign ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={campaign.status} />
            <span className="text-xs text-slate-500">{campaign.refNo}</span>
          </div>
          {campaign.description ? <p className="text-sm text-slate-700">{campaign.description}</p> : null}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-500">Budget envelope</dt>
              <dd className="font-medium text-slate-800">{scr(campaign.budgetEnvelopeScr)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Voting opens</dt>
              <dd className="font-medium text-slate-800">{formatDateTime(campaign.opensAt)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Voting closes</dt>
              <dd className="font-medium text-slate-800">{formatDateTime(campaign.closesAt)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Total allocated</dt>
              <dd className="font-medium text-slate-800">{campaign.totalAllocatedScr != null ? scr(campaign.totalAllocatedScr) : '—'}</dd>
            </div>
          </dl>

          {campaign.status === 'CANCELLED' && campaign.cancelReason ? <ErrorNote message={`Cancelled: ${campaign.cancelReason}`} /> : null}
          {campaign.status === 'FINALIZED' ? (
            <p className="text-xs text-slate-500">
              Finalized {formatDateTime(campaign.finalizedAt)} by {campaign.finalizedBy}.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {isAdmin && campaign.status === 'DRAFT' ? (
              <button className="btn-primary" disabled={busy} onClick={open}>
                Open voting
              </button>
            ) : null}
            {isAdmin && campaign.status === 'OPEN' ? (
              <button className="btn-primary" disabled={busy} onClick={close}>
                Close voting
              </button>
            ) : null}
            {isAdmin && campaign.status === 'CLOSED' ? (
              <button className="btn-success" disabled={busy} onClick={finalize}>
                Finalize &amp; fund
              </button>
            ) : null}
            {isAdmin && (campaign.status === 'DRAFT' || campaign.status === 'OPEN') ? (
              <button className="btn-danger" disabled={busy} onClick={() => setShowCancel(true)}>
                Cancel campaign
              </button>
            ) : null}
            {!isAdmin ? <p className="text-xs text-slate-500">Only this district's Administrator can change a campaign's lifecycle.</p> : null}
          </div>

          {showCancel ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <Field label="Cancellation reason">
                <textarea className="field-input" rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowCancel(false)}>
                  Back
                </button>
                <button className="btn-danger" disabled={busy || cancelReason.trim().length < 3} onClick={cancel}>
                  Confirm cancel
                </button>
              </div>
            </div>
          ) : null}

          {finalizeResult ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
              Finalized: {finalizeResult.selectedIds.length} project(s) funded, {finalizeResult.notSelectedIds.length} not
              selected. An OpenGov gazette results notice was published and every proposer notified.
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3">
            <h3 className="mb-2 text-sm font-bold text-slate-700">Shortlisted proposals — live vote tally</h3>
            {projects.length === 0 ? (
              <EmptyState title="No proposals shortlisted yet" hint="Shortlist UNDER_REVIEW proposals from the Community Projects screen." />
            ) : (
              <ul className="space-y-2.5">
                {projects.map((p) => (
                  <li key={p.id}>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                      <span className="font-medium text-slate-700">{p.title}</span>
                      <span className="flex items-center gap-2">
                        <StatusBadge status={p.status} />
                        <span className="tabular-nums text-slate-500">
                          {p.voteCount} votes · {scr(p.estimatedCostScr)}
                        </span>
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.max(4, (p.voteCount / maxVotes) * 100)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
